//@ts-nocheck
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CollapsibleCard from "components/CollapsibleCard";
import moment from "moment";
import { calculateWorkingDays, getCalendarDateFromBusinessDays } from "utils/CalanderUtils";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { get, post, request } from "../../../../../requests";
import { useStoreState, useStoreActions } from "easy-peasy";
import { useSubmissionStore } from "models/applications/SubmissionStore";
import FundingChart from "../../underwriting/FundingChart";
import { convertNumeric } from "utils/CommonUtils";
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import StorageUtils from "utils/StorageUtils";
import {
  Add,
  AddBox,
  AddCircleOutline,
  AddCircleTwoTone,
  Download,
  Expand,
  ExpandMore,
  PlayCircle,
  PostAdd,
} from "@mui/icons-material";
import MDSelect from "components/MDSelect";
import { downloadWithFileName, downloadWithFileNameAsync } from "utils/FileDownloadUtils";
import FullPageLoader from "components/Loader/FullPage";
import Loader from "components/Loader/Basic";

const HeaderSection: React.FC<{ label: string; value: string; style?: any | null }> = ({
  label,
  value,
  style,
}) => (
  <MDBox display="flex" flexDirection="column" alignItems="center">
    <MDTypography
      variant="body2"
      sx={{
        color: "#7B809A",
        textAlign: "center",
        fontFamily: "Roboto",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "140%",
      }}
    >
      {label}
    </MDTypography>
    <MDTypography
      variant="h6"
      sx={{
        color: "#344767",
        textAlign: "center",
        fontFamily: "Roboto",
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "140%",
        ...style,
      }}
    >
      {value}
    </MDTypography>
  </MDBox>
);

interface SummaryCardProps {
  dealApplicationId: any;
  allowButton: Boolean;
  allowResubmit: Boolean;
  onResubmit: () => void;
  allowSummarySelection: any;
  refreshDataBasedOnSource: (source: string) => void;
  recurringDebits: any;
  loading: boolean;
}

const OcrolusSummaryCard: React.FC<SummaryCardProps> = ({
  dealApplicationId,
  allowButton = true,
  allowSummarySelection = true,
  allowResubmit = false,
  onResubmit = undefined,
  refreshDataBasedOnSource,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("weekly");

  const bankStatementsSummary = useStoreState(
    (state: any) => state.bankSummaryStore.bankStatementData
  );
  const advancesData = useStoreState((state: any) => state.bankSummaryStore.advancesData);
  const positionCount = useStoreState((state: any) => state.bankSummaryStore.positionCount);
  const scoreboard = useStoreState((state: any) => state.bankSummaryStore.scoreboard);
  const setScoreboard = useStoreActions((actions: any) => actions.bankSummaryStore.setScoreboard);

  const contractTerms = useStoreState((state: any) => state.offerStore.contractTerms);
  const [AVAILABLE_HOLDBACK, SET_AVAILABLE_HOLDBACK] = useState(
    contractTerms?.contractTerms?.funder?.maxHoldback || 0
  );

  const { setSelectedTab } = useStoreActions((actions: any) => actions.dealApplication);
  const { selectedAnalysisTab } = useStoreState((state: any) => state.dealApplication);
  const { prevSelectedAnalysisTab } = useStoreState((state: any) => state.dealApplication);
  const { dealApplicationData } = useSubmissionStore();
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setScoreboard(null);
    calculateScoreboardData();
  }, [bankStatementsSummary, advancesData, positionCount, selectedAnalysisTab]);

  const isLoading = false;

  const parseMoneyThumbScoreboard = (response, availableHoldbackLimit) => {
    const stats = response?.result?.["Revenue Statistics"] || [];
    const mcaCompanies = response?.result?.["MCA Companies"] || [];

    const find = (label) => stats.find((i) => i.Label?.toLowerCase() === label.toLowerCase());

    const num = (v) => (v ? Number(String(v).replace(/[^0-9.-]/g, "")) || 0 : 0);

    const revenue = find("Revenue");
    const trueRevenue = find("True Revenue");
    const expenses = find("Expenses");
    const profit = find("Profit");
    const nonTrueRevenue = find("Non-True Revenue");
    const debt = find("Total Debt Withdrawals");
    const mcaHold = find("MCA Withhold Percent");

    const mca = num(mcaHold?.Annual);
    const available = Number((availableHoldbackLimit - mca).toFixed(2));

    const positions = (mcaCompanies || []).length;

    return {
      averageMonthlyRevenue: num(revenue?.Monthly),
      averageTrueDeposits: num(trueRevenue?.Monthly),
      averageNonTrueDeposits: num(nonTrueRevenue?.Monthly),
      averageMonthlyExpenses: num(expenses?.Monthly),
      averageGrossProfit: num(profit?.Monthly),
      totalMonthlyDebits: num(debt?.Monthly),
      monthlyMCAHoldback: mca,
      availableHoldBack: available,
      positions: positions,
    };
  };

  const downloadOcrolusAnalytics = async () => {
    try {
      setDownloading(true);

      const url = `/api/integration/ocrolous/${dealApplicationId}/analytics/xlsx`;

      const fileName = dealApplicationData?.applicationNo
        ? `Ocrolus_Analytics_${dealApplicationData.applicationNo}.xlsx`
        : `Ocrolus_Analytics_${dealApplicationId}.xlsx`;

      await downloadWithFileNameAsync(url, fileName);
    } catch (e) {
      console.error("Failed to download Ocrolus analytics", e);
    } finally {
      setDownloading(false);
    }
  };

  const calculateScoreboardData = useCallback(() => {
    if (bankStatementsSummary && bankStatementsSummary.length > 0) {
      let totalRevenue = 0;
      let totalTrueDeposits = 0;
      let totalNonTrueDeposits = 0;
      let totalExpenses = 0;
      let totalGrossProfit = 0;
      let mcaHoldback = 0;
      let totalMonthlyDebits = 0;
      let averageMonthlyDebits = 0;

      advancesData
        .filter((adv) => adv?.selected)
        .forEach((advance: any) => {
          totalMonthlyDebits += convertNumeric(advance.monthlyAmount) || 0;
        });

      bankStatementsSummary
        .filter((summary: any) => summary.selected)
        .forEach((statement: any) => {
          totalRevenue += convertNumeric(statement.totalCredits) || 0;
          totalTrueDeposits += convertNumeric(statement.totalTrueCredits) || 0;
          totalNonTrueDeposits +=
            convertNumeric(statement.totalCredits) - convertNumeric(statement.totalTrueCredits) ||
            0;
          totalExpenses += convertNumeric(statement.totalDebits) || 0;
          totalGrossProfit +=
            convertNumeric(statement.totalCredits) + convertNumeric(statement.totalDebits) || 0;
        });

      const numStatements = (
        bankStatementsSummary?.filter((summary: any) => summary.selected) || []
      ).length;

      const averageTrueDeposits = numStatements > 0 ? totalTrueDeposits / numStatements : 0;

      if (numStatements > 0) {
        averageMonthlyDebits = Math.round((totalMonthlyDebits / numStatements) * 100) / 100;
      } else {
        averageMonthlyDebits = totalMonthlyDebits;
      }

      if (averageTrueDeposits > 0) {
        mcaHoldback = (averageMonthlyDebits / averageTrueDeposits) * 100;
        mcaHoldback = parseFloat(mcaHoldback.toFixed(2));
      }

      setScoreboard({
        averageMonthlyRevenue:
          numStatements > 0 ? Math.round((totalRevenue / numStatements) * 100) / 100 : 0,
        averageTrueDeposits:
          numStatements > 0 ? Math.round((totalTrueDeposits / numStatements) * 100) / 100 : 0,
        averageNonTrueDeposits:
          numStatements > 0 ? Math.round((totalNonTrueDeposits / numStatements) * 100) / 100 : 0,
        averageMonthlyExpenses:
          numStatements > 0 ? Math.round((totalExpenses / numStatements) * 100) / 100 : 0,
        averageGrossProfit:
          numStatements > 0 ? Math.round((totalGrossProfit / numStatements) * 100) / 100 : 0,
        totalMonthlyDebits: Math.round(averageMonthlyDebits * 100) / 100,
        monthlyMCAHoldback: Math.round(mcaHoldback * 100) / 100,
        availableHoldBack:
          Math.round(((AVAILABLE_HOLDBACK || 0) - (mcaHoldback ? mcaHoldback : 0)) * 100) / 100,
        positions: positionCount,
      });
    }
  }, [bankStatementsSummary, positionCount, advancesData, AVAILABLE_HOLDBACK]);

  const customButton = (
    <MDBox display="flex" alignItems="center" gap={2}>
      {allowResubmit && (
        <MDBox>
          <Tooltip title="Add Statements" placement="bottom" arrow>
            <Add
              onClick={onResubmit}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontSize: "26px !important",
                "& svg": {
                  strokeWidth: 3,
                  stroke: "currentColor",
                  filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.1))",
                },
                "&:hover": {
                  color: "primary.dark",
                  "& svg": {
                    strokeWidth: 5,
                  },
                },
              }}
            />
          </Tooltip>
        </MDBox>
      )}

      <Tooltip title="Download Ocrolus Analytics (Excel)" arrow>
        <span>
          <IconButton
            size="small"
            onClick={downloadOcrolusAnalytics}
            disabled={downloading}
            sx={{
              color: "primary.main",
              fontSize: "24px !important",
              "&:hover": { color: "primary.dark" },
            }}
          >
            <Download fontSize="24px !important" />
          </IconButton>
        </span>
      </Tooltip>

      {allowButton && (
        <MDButton
          color="primary"
          variant="contained"
          size="medium"
          sx={{ textTransform: "none" }}
          onClick={() => {
            setScheduleFrequency("weekly");
            setOpen(true);
          }}
        >
          Funding Calculator
        </MDButton>
      )}
    </MDBox>
  );

  return (
    <>
      <CollapsibleCard header={"Summary"} customButton={customButton}>
        {loading ? (
          <MDBox
            paddingRight={3}
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Loader />
          </MDBox>
        ) : (
          <MDBox
            paddingLeft={4}
            paddingRight={3}
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <HeaderSection
              label="Average Monthly Revenue"
              value={formatCurrencyUSD(scoreboard?.averageMonthlyRevenue)}
            />

            <HeaderSection
              label="Average True Deposits"
              value={formatCurrencyUSD(scoreboard?.averageTrueDeposits)}
            />

            <HeaderSection
              label="Average Monthly Debits"
              value={formatCurrencyUSD(scoreboard?.totalMonthlyDebits)}
            />

            <HeaderSection
              label="Average Non-True Deposits"
              value={formatCurrencyUSD(scoreboard?.averageNonTrueDeposits)}
            />

            <HeaderSection
              label="Average Monthly Expenses"
              value={formatCurrencyUSD(scoreboard?.averageMonthlyExpenses)}
            />

            <HeaderSection
              label="Average Gross Profit"
              value={formatCurrencyUSD(scoreboard?.averageGrossProfit)}
            />

            <HeaderSection
              label="MCA Holdback"
              value={scoreboard?.monthlyMCAHoldback ? `${scoreboard?.monthlyMCAHoldback}%` : ""}
            />

            <HeaderSection
              label="Available Holdback"
              value={scoreboard?.availableHoldBack ? `${scoreboard?.availableHoldBack}%` : ""}
            />

            <HeaderSection label="Positions" value={scoreboard?.positions} />
            {downloading && <FullPageLoader />}
          </MDBox>
        )}
      </CollapsibleCard>

      {open && (
        <FundingChart
          open={open}
          setOpen={setOpen}
          mode="add"
          formData={formData}
          setFormData={setFormData}
          debits={debits}
          scoreboard={scoreboard}
          setDebits={setDebits}
          dealApplicationId={dealApplicationId}
          scheduleFrequency={scheduleFrequency}
          setScheduleFrequency={setScheduleFrequency}
        />
      )}
    </>
  );
};

export default OcrolusSummaryCard;
